import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { FbrStatusEnum, FbrClientModel, FbrQuestionnaireTypesEnum, FbrItemOutcomeStepEnum } from "../../../../../models/fbrs/fbr.models";
import { NotificationClientService } from "../../../../../services/basics/notification-client.service";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { Claims } from "../../../../../constants/authentication/claims";
import { ChildComponent } from "../../child-component";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import * as moment from "moment";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Reflection } from "../../../../../utilities/reflection";
import { ClientSideValidator, EntityValidationsService } from "../../../../../client-validation-system/validations-service";
import { RulesHolder, ValidationActivation, ValidationTypeEnum } from "../../../../../client-validation-system/validations.models";
import { FbrService } from "../../../../../services/fbr/fbr.service";
import { ManageRightShipUsersClientService } from "../../../../../services/modules/righship-qi-manage-users.service";
import { UserClientService } from "../../../../../services/authentication/user-client.service";

@autoinject
@transient()
@useView('views/pages/modules/fbr/tabs/fbr-overview.html')
export class FbrOverview extends ChildComponent<FbrClientModel>{
    private fbrStatuses: LookupValue[];
    public isRightShipUser: boolean;
    public terminalCanTakeDecision: boolean;
    statusSnippet: string;

    public customerId: number;
    reassignment: FbrStatusEnum = FbrStatusEnum.FbrReassignment;
    protected accessableBusinessUnitIds: number[] = [];

    constructor(
        notificationsService: NotificationClientService, lookupClientService: LookupClientService,
        private userClaimsService: UserClaimsService,
        private dialogClientService: DialogClientService,
        private fbrService: FbrService, private entityValidationsService: EntityValidationsService,
        private manageRightShipUsersClientService: ManageRightShipUsersClientService,
        private userClientService: UserClientService,
        i18N: I18N, private router: Router) {
        super(notificationsService, i18N);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.FbrStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.fbrStatuses = x.activeValues;
        });
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.terminalCanTakeDecision = this.userClaimsService.hasPermission(Claims.TerminalCanTakeDecision);
    }

    @computedFrom(Reflection.getPropertyPath<FbrOverview>(z => z.item),
    Reflection.getPropertyPath<FbrOverview>(z => z.item.fbrItems),
    Reflection.getPropertyPath<FbrOverview>(z => z.item.fbrItems.length))
    get anyIncidents() {
        return this.item.fbrItems.filter(x => x.linkedIncidents.length > 0).length > 0;
    }
 
    get awaitingResponse() {
        return this.item && (this.item.fbrStatusId == FbrStatusEnum.AwaitingResponse);
    }
 
    get fbrStatus() {
        if (this.fbrStatuses && this.item)
            return this.fbrStatuses.filter(x => x.id == this.item.fbrStatusId)[0];

        return null;
    }

    @computedFrom("item", "item.built")
    get built() {
        const date = moment(this.item.built);
        const yearAge = moment().diff(date, 'year', true).toFixed(1) + "y/o" + " (" + date.year() + ")";
        return yearAge;
    }

    @computedFrom("item", "item.fbrStatusHistory", "item.fbrStatusHistory.length", "item.fbrStatusId")
    get lastFbrStatusHistory() {
        return this.item.fbrStatusHistory[0];
    }

    @computedFrom(Reflection.getPropertyPath<FbrOverview>(v => v.item), Reflection.getPropertyPath<FbrOverview>(v => v.item.fbrStatusId))
    public get isPendingTerminalReview(): boolean {
        return this.item && this.item.fbrStatusId == FbrStatusEnum.PendingTerminalReview;
    }

    navigateToVessel() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselDetails, { id: this.item.vesselId });
    }

    @computedFrom(Reflection.getPropertyPath<FbrOverview>(z => z.item),
    Reflection.getPropertyPath<FbrOverview>(z => z.item.fbrItems),
    Reflection.getPropertyPath<FbrOverview>(z => z.item.fbrItems.length))
    get reviewRequired(): number {
        return this.item.fbrItems.filter(x => x.rightShipReviewRequired && x.outcome == FbrItemOutcomeStepEnum.Fail).length;
    }
    
    @computedFrom(Reflection.getPropertyPath<FbrOverview>(z => z.item),
    Reflection.getPropertyPath<FbrOverview>(z => z.item.fbrItems),
    Reflection.getPropertyPath<FbrOverview>(z => z.item.fbrItems.length))
    get reviewNotRequired(): number {
        return this.item.fbrItems.filter(x => !x.rightShipReviewRequired && x.outcome == FbrItemOutcomeStepEnum.Fail).length;
    }

    @computedFrom(Reflection.getPropertyPath<FbrOverview>(z => z.reviewRequired),
    Reflection.getPropertyPath<FbrOverview>(z => z.reviewNotRequired))
    get unsatisfactoryObservations(): number {
        return this.reviewRequired + this.reviewNotRequired;
    }

    @computedFrom("item", "item.fbrQuestionnaireType")
    get isBerthFBR() {
        return this.item && this.item.fbrQuestionnaireType == FbrQuestionnaireTypesEnum.DryBerthReport;
    }

    @computedFrom("item", "item.fbrQuestionnaireType")
    get isLoadingFBR() {
        return this.item && this.item.fbrQuestionnaireType == FbrQuestionnaireTypesEnum.LoadingReport;
    }

    @computedFrom("item", "item.fbrQuestionnaireType")
    get isTankerFBR() {
        return this.item && this.item.fbrQuestionnaireType == FbrQuestionnaireTypesEnum.TankerTerminalReport;
    }

    showContactVetSuperDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddCorrespondenceDialog, {
            entityId: this.item.fbrId,
            entityTypeId: EntityTypes.FBR
        }, true, false, false, "right");
    }

    @computedFrom(Reflection.getPropertyPath<FbrOverview>(v => v.item), Reflection.getPropertyPath<FbrOverview>(v => v.item.fbrStatusId))
    public get isClosed(): boolean {
        return this.item && this.item.fbrStatusId == FbrStatusEnum.Closed;
    }

    @computedFrom(Reflection.getPropertyPath<FbrOverview>(v => v.item), Reflection.getPropertyPath<FbrOverview>(v => v.item.fbrStatusId))
    public get isCancelled(): boolean {
        return this.item && this.item.fbrStatusId == FbrStatusEnum.Cancelled;
    }

    @computedFrom(Reflection.getPropertyPath<FbrOverview>(v => v.item), Reflection.getPropertyPath<FbrOverview>(v => v.item.requestedByBusinessUnitId), Reflection.getPropertyPath<FbrOverview>(v => v.customerId))
    public get isCreatingCustomer(): boolean {
        return this.item && this.item.requestedOnBehalfOfCustomerId == this.customerId;
    }

    @computedFrom(Reflection.getPropertyPath<FbrOverview>(v => v.isPendingTerminalReview), Reflection.getPropertyPath<FbrOverview>(v => v.terminalCanTakeDecision),
        Reflection.getPropertyPath<FbrOverview>(v => v.item.requestedByBusinessUnitId), Reflection.getPropertyPath<FbrOverview>(v => v.accessableBusinessUnitIds),
        Reflection.getPropertyPath<FbrOverview>(v => v.isRightShipUser))
    public get canTakeTerminalDecision(): boolean {
        return this.isPendingTerminalReview && this.terminalCanTakeDecision 
            && (this.isRightShipUser || (this.item.requestedByBusinessUnitId && this.accessableBusinessUnitIds && this.accessableBusinessUnitIds.indexOf(this.item.requestedByBusinessUnitId) > -1));
    }
    
    validator: ClientSideValidator;
    private setValidationRules() {
        if (this.validator && this.validator.dispose)
            this.validator.dispose();

        const rulesHolder: RulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "Change FBR Status",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Comments is required",
                parameters: null, property: "statusSnippet",
                validationType: ValidationTypeEnum.Required
            }]
        };
        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    reload: () => void;

    public activate(model: { item: FbrClientModel, reload: () => void, customerId: number }): void {
        super.activate(model.item);
        this.reload = model.reload;
        this.customerId = model.customerId;
    }

    attached() {
        this.manageRightShipUsersClientService.getBusinessUnitUser(this.userClientService.currentUser.id)
            .then(user => {
                const businessUnitId = user.businessUnit ? user.businessUnit.businessUnitId : null;
                const accessableBusinessUnitIds: number[] =  user.businessUnitUserPermissions? user.businessUnitUserPermissions.map(x => x.businessUnitId): [];
                accessableBusinessUnitIds.push(businessUnitId)

                this.accessableBusinessUnitIds = accessableBusinessUnitIds;
            });
    }

    async terminalTakeDecision(status: number){
        this.setValidationRules();

        if (this.validator) {
            const validationResponse = await this.validator.validate();
            if (!validationResponse.valid) return;
        }
        try {
            await this.fbrService.terminalTakeDecision(this.item.fbrId, status, this.statusSnippet);
            this.statusSnippet = null;
            if(this.reload) this.reload();
        } catch (e) {
            console.log("Failed to post terminal take decision", e);
        }
    }
}